/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig, Method, AxiosRequestHeaders } from "axios";
import { createApiUrl } from "utils/urls";

const getData = <T>(response: AxiosResponse<T>) => response.data;

const defaultTimeout = 10000;

export const STATUS_NOT_FOUND = 404;
export const STATUS_GONE = 410;

export interface ErrorResponseData {
  error: { code: string; message: string; status: number };
}

export abstract class BaseHttpService {
  private readonly axiosClient: AxiosInstance;

  private readonly promiseRequests: {[key: string]: Promise<any> } = {};

  protected constructor (readonly baseURL?: string) {
    this.axiosClient = axios.create({
      baseURL: baseURL ?? process.env.REACT_APP_API_BASE_URL ?? "/",
      responseType: "json",
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public async get(url: string, headers?: AxiosRequestHeaders): Promise<any> {
    const key = JSON.stringify({
      method: "get",
      url,
      headers
    });

    if (key in this.promiseRequests) {
      return this.promiseRequests[key];
    }

    this.promiseRequests[key] = this.request("get", url, undefined, headers);

    try {
      const response = await this.promiseRequests[key];
      return response;
    } finally {
      delete this.promiseRequests[key];
    }
  }

  /**
   * Send a beacon, when available, otherwise a regular POST.
   */
  protected async beacon(path: string, data?: any): Promise<any> {
    if (navigator.sendBeacon && window.Blob) {
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const url = createApiUrl(path);
      navigator.sendBeacon(url, blob);
    } else {
      return await this.request("post", path, data);
    }
  }

  protected async post(url: string, data?: any, headers?: AxiosRequestHeaders){
    return await this.request("post", url, data, headers);
  }

  protected async patch(url: string, data?: any, headers?: AxiosRequestHeaders){
    return await this.request("patch", url, data, headers);
  }

  protected async put(url: string, data?: any, headers?: AxiosRequestHeaders){
    return await this.request("put", url, data, headers);
  }

  protected async delete(url: string, data?: any, headers?: AxiosRequestHeaders){
    return await this.request("delete", url, data, headers);
  }

  protected request(method: Method, url: string, data?: any, headers?: AxiosRequestHeaders): Promise<any> {
    // eslint-disable-next-line import/no-named-as-default-member
    const source = axios.CancelToken.source();

    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      headers,
      cancelToken: source.token
    };

    setTimeout(() => {
      source.cancel("Internal Server Error");
    }, defaultTimeout);

    return this.axiosClient
      .request(config)
      .then(getData);
  }
}
